import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, TableSortLabel,
    Chip, IconButton, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, MenuItem, Snackbar, Alert, InputAdornment, Tabs, Tab, Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory2';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ArticleIcon from '@mui/icons-material/Article';
import { usePermisos } from '../../utils/usePermisos';
import {
    custodiaEstadosJWT, custodiaMovTiposJWT, custodiaStatsJWT,
    custodiaListarJWT, custodiaCrearJWT, custodiaEditarJWT, custodiaAnularJWT,
    custodiaMovimientosJWT, custodiaAgregarMovJWT,
    custodiaDocumentosJWT, custodiaAgregarDocJWT, custodiaQuitarDocJWT
} from '../../api/endpoints';
import { nodeApiPath } from '../../utils/paths';

const CUST_COLOR = '#455a64';
const CUST_LIGHT = '#eceff1';

const titleCase = (str) => {
    if (!str) return '';
    // No normalizar siglas (contienen puntos como F.E.) o textos cortos
    if (str.includes('.') || str.length <= 5) return str;
    if (str === str.toUpperCase() && str.length > 5) {
        return str.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
    }
    return str;
};

const estadoColor = (id) => {
    const colors = { '-1': 'error', '1': 'warning', '2': 'info', '3': 'success', '4': 'secondary' };
    return colors[String(id)] || 'default';
};

export default function CustodiaHome() {
    const { tienePermiso } = usePermisos();
    const puedeCrear = tienePermiso('CUSTODIA DE ARCHIVOS', 'INSERT');
    const puedeEditar = tienePermiso('CUSTODIA DE ARCHIVOS', 'UPDATE');
    const puedeEliminar = tienePermiso('CUSTODIA DE ARCHIVOS', 'DELETE');

    const [loading, setLoading] = useState(false);
    const [cajas, setCajas] = useState([]);
    const [total, setTotal] = useState(0);
    const [estados, setEstados] = useState([]);
    const [movTipos, setMovTipos] = useState([]);
    const [stats, setStats] = useState({});

    const [busqueda, setBusqueda] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState('todos');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [orderBy, setOrderBy] = useState('Id');
    const [order, setOrder] = useState('desc');

    // Dialogs
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('crear');
    const [formData, setFormData] = useState({ contenido: '', precinto: '', vencimiento: '' });
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, caja: null });

    // Detalle dialog
    const [detalleOpen, setDetalleOpen] = useState(false);
    const [detalleCaja, setDetalleCaja] = useState(null);
    const [detalleTab, setDetalleTab] = useState(0);
    const [detalleDocumentos, setDetalleDocumentos] = useState([]);
    const [detalleMovimientos, setDetalleMovimientos] = useState([]);
    const [detalleLoading, setDetalleLoading] = useState(false);

    // Movimiento dialog
    const [movDialog, setMovDialog] = useState(false);
    const [movForm, setMovForm] = useState({ descripcion: '', tipoMovimiento: -1, estado: 1, fecha: new Date().toISOString().split('T')[0] });

    // Agregar doc dialog
    const [docDialog, setDocDialog] = useState(false);
    const [docForm, setDocForm] = useState({ tipoDoc: 1, idDoc: '', observaciones: '', fechaArchivo: new Date().toISOString().split('T')[0] });
    const [docSearchOptions, setDocSearchOptions] = useState([]);
    const [docSearching, setDocSearching] = useState(false);
    const docSearchTimer = React.useRef(null);

    // Confirmación desvincular
    const [desvinculaDialog, setDesvinculaDialog] = useState({ open: false, id: null, desc: '' });

    const [snack, setSnack] = useState({ open: false, text: '', severity: 'success' });

    const handleDocSearch = (q) => {
        if (docSearchTimer.current) clearTimeout(docSearchTimer.current);
        if (!q || q.length < 2) { setDocSearchOptions([]); return; }
        docSearchTimer.current = setTimeout(async () => {
            setDocSearching(true);
            try {
                const token = localStorage.getItem('SIGIF_JWT_TOKEN');
                let endpoint = '';
                if (docForm.tipoDoc === 1) endpoint = `expedientes/buscar-judiciales?q=${encodeURIComponent(q)}`;
                else if (docForm.tipoDoc === 2) endpoint = `expedientes/buscar-administrativos?q=${encodeURIComponent(q)}`;
                else endpoint = `biblioteca?busqueda=${encodeURIComponent(q)}&pageSize=10`;
                const res = await fetch(nodeApiPath(endpoint), { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                if (docForm.tipoDoc === 3) {
                    setDocSearchOptions((data.documentos || []).map(d => ({ id: String(d.Id), label: `${d.Codigo} - ${titleCase(d.Descripcion || '')}` })));
                } else if (docForm.tipoDoc === 2) {
                    setDocSearchOptions((data.expedientes || []).map(e => ({
                        id: String(e.Id), label: `${e.Expediente_Adm || ''} - ${titleCase(e.Extracto || e.Caratula || '')}`
                    })));
                } else {
                    setDocSearchOptions((data.expedientes || []).map(e => ({
                        id: String(e.Id), label: `${e.Autos_1_Instancia || ''} - ${titleCase(e.Caratula || '')}`
                    })));
                }
            } catch { setDocSearchOptions([]); }
            setDocSearching(false);
        }, 300);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const r = await custodiaListarJWT({ busqueda, estadoId: estadoFiltro, page: page + 1, pageSize });
            if (r.ok) { setCajas(r.cajas || []); setTotal(r.total || 0); }
        } catch { /* interceptor */ }
        setLoading(false);
    }, [busqueda, estadoFiltro, page, pageSize]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        Promise.all([custodiaEstadosJWT(), custodiaMovTiposJWT(), custodiaStatsJWT()])
            .then(([e, m, s]) => {
                if (e?.ok) setEstados(e.estados || []);
                if (m?.ok) setMovTipos(m.tipos || []);
                if (s?.ok) setStats(s);
            }).catch(() => {});
    }, []);

    const handleSort = (col) => {
        setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(col);
    };

    const sorted = [...cajas].sort((a, b) => {
        let aV = a[orderBy], bV = b[orderBy];
        if (typeof aV === 'string') { aV = aV?.toLowerCase() || ''; bV = bV?.toLowerCase() || ''; }
        if (aV < bV) return order === 'asc' ? -1 : 1;
        if (aV > bV) return order === 'asc' ? 1 : -1;
        return 0;
    });

    // CRUD handlers
    const handleNueva = () => {
        setDialogMode('crear'); setFormData({ contenido: '', precinto: '', vencimiento: '' });
        setEditId(null); setDialogOpen(true);
    };

    const parseDateToInput = (d) => {
        if (!d) return '';
        const parts = d.split('/');
        if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
        return d;
    };

    const handleEditar = (caja) => {
        setDialogMode('editar');
        setFormData({ contenido: caja.Contenido || '', precinto: caja.Precinto || '', vencimiento: parseDateToInput(caja.Vencimiento) });
        setEditId(caja.Id); setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.contenido) { setSnack({ open: true, text: 'Contenido es obligatorio', severity: 'warning' }); return; }
        setSaving(true);
        try {
            const r = dialogMode === 'crear' ? await custodiaCrearJWT(formData) : await custodiaEditarJWT(editId, formData);
            if (r?.ok) {
                setSnack({ open: true, text: dialogMode === 'crear' ? 'Caja creada' : 'Caja actualizada', severity: 'success' });
                setDialogOpen(false); fetchData();
                custodiaStatsJWT().then(s => { if (s?.ok) setStats(s); });
            } else { setSnack({ open: true, text: r?.mensaje || 'Error', severity: 'error' }); }
        } catch { setSnack({ open: true, text: 'Error de conexión', severity: 'error' }); }
        setSaving(false);
    };

    const handleAnular = async () => {
        if (!deleteDialog.caja) return;
        try {
            const r = await custodiaAnularJWT(deleteDialog.caja.Id);
            if (r?.ok) { setSnack({ open: true, text: 'Caja anulada', severity: 'success' }); setDeleteDialog({ open: false, caja: null }); fetchData(); }
            else { setSnack({ open: true, text: r?.mensaje || 'Error', severity: 'error' }); }
        } catch { setSnack({ open: true, text: 'Error', severity: 'error' }); }
    };

    // Detalle
    const handleVerCaja = async (caja) => {
        setDetalleCaja(caja); setDetalleOpen(true); setDetalleTab(0); setDetalleLoading(true);
        try {
            const [docs, movs] = await Promise.all([custodiaDocumentosJWT(caja.Id), custodiaMovimientosJWT(caja.Id)]);
            if (docs?.ok) setDetalleDocumentos(docs.documentos || []);
            if (movs?.ok) setDetalleMovimientos(movs.movimientos || []);
        } catch { /* ignore */ }
        setDetalleLoading(false);
    };

    const handleAgregarMov = async () => {
        try {
            const r = await custodiaAgregarMovJWT(detalleCaja.Id, movForm);
            if (r?.ok) {
                setSnack({ open: true, text: 'Movimiento registrado', severity: 'success' });
                setMovDialog(false); handleVerCaja(detalleCaja); fetchData();
            }
        } catch { setSnack({ open: true, text: 'Error', severity: 'error' }); }
    };

    const handleAgregarDoc = async () => {
        if (!docForm.idDoc) { setSnack({ open: true, text: 'Ingrese ID del documento', severity: 'warning' }); return; }
        try {
            const r = await custodiaAgregarDocJWT(detalleCaja.Id, docForm);
            if (r?.ok) {
                setSnack({ open: true, text: 'Documento asignado', severity: 'success' });
                setDocDialog(false); handleVerCaja(detalleCaja); fetchData();
            } else { setSnack({ open: true, text: r?.mensaje || 'Error', severity: 'error' }); }
        } catch { setSnack({ open: true, text: 'Error', severity: 'error' }); }
    };

    const handleQuitarDoc = async (idRelacion) => {
        try {
            const r = await custodiaQuitarDocJWT(idRelacion);
            if (r?.ok) { setSnack({ open: true, text: 'Documento desvinculado', severity: 'success' }); handleVerCaja(detalleCaja); fetchData(); }
        } catch { /* ignore */ }
    };

    return (
        <Box>
            {/* Header */}
            <Paper sx={{ borderRadius: '4px', overflow: 'hidden', mb: 2 }}>
                <Box sx={{ background: `linear-gradient(135deg, ${CUST_COLOR} 0%, #607d8b 100%)`, px: 3, py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InventoryIcon /> Custodia de Archivos
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Gestión de cajas y documentos archivados
                            </Typography>
                        </Box>
                        {puedeCrear && (
                            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNueva}
                                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}>
                                Nueva Caja
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Stats */}
                <Box sx={{ display: 'flex', gap: 2, p: 2, backgroundColor: CUST_LIGHT }}>
                    {[
                        { label: 'Total Cajas', value: stats.total_cajas, color: CUST_COLOR, filtro: 'todos' },
                        { label: 'En Preparación', value: stats.en_preparacion, color: '#f57c00', filtro: '1' },
                        { label: 'Activas', value: stats.activas, color: '#2e7d32', filtro: 'activas' },
                        { label: 'Documentos', value: stats.total_documentos, color: '#1769aa', filtro: null },
                    ].map((s, i) => (
                        <Paper key={i} onClick={() => { if (s.filtro !== null) { setEstadoFiltro(s.filtro); setPage(0); } }}
                            sx={{
                                p: 1.5, flex: 1, textAlign: 'center', borderRadius: '4px',
                                cursor: s.filtro !== null ? 'pointer' : 'default',
                                transition: 'transform 0.2s',
                                border: estadoFiltro === s.filtro ? `2px solid ${s.color}` : '2px solid transparent',
                                '&:hover': s.filtro !== null ? { transform: 'translateY(-2px)', boxShadow: 2 } : {},
                            }}>
                            <Typography variant="h5" fontWeight={700} color={s.color}>{s.value || 0}</Typography>
                            <Typography variant="caption">{s.label}</Typography>
                        </Paper>
                    ))}
                </Box>

                {/* Filtros */}
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={1.5} alignItems="center">
                        <Grid size={{ xs: 12, sm: 5 }}>
                            <TextField size="small" fullWidth placeholder="Buscar por Nº caja, contenido, responsable..."
                                value={busqueda} onChange={e => setBusqueda(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && fetchData()}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <TextField select size="small" fullWidth label="Estado" value={estadoFiltro}
                                onChange={e => { setEstadoFiltro(e.target.value); setPage(0); }}>
                                <MenuItem value="todos">Todos</MenuItem>
                                {estados.map(e => <MenuItem key={e.Id} value={e.Id}>{titleCase(e.Estado)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <Button variant="contained" fullWidth size="small" startIcon={<SearchIcon />} onClick={() => { setPage(0); fetchData(); }}
                                sx={{ height: 40, backgroundColor: CUST_COLOR, '&:hover': { backgroundColor: '#37474f' } }}>
                                Buscar
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* Tabla */}
            <TableContainer component={Paper} sx={{ borderRadius: '4px' }}>
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: CUST_LIGHT }}>
                                {[
                                    { id: 'Id', label: 'Nº Caja' },
                                    { id: 'Contenido', label: 'Contenido' },
                                    { id: 'Estado', label: 'Estado' },
                                    { id: 'Ubicacion', label: 'Ubicación' },
                                    { id: 'Total_Documentos', label: 'Docs' },
                                    { id: 'Apertura', label: 'Apertura' },
                                    { id: 'Responsable', label: 'Responsable' },
                                ].map(col => (
                                    <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                                        <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} onClick={() => handleSort(col.id)}>
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                {(puedeEditar || puedeEliminar) && <TableCell align="center">Acciones</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No se encontraron cajas</TableCell></TableRow>
                            ) : sorted.map((caja, idx) => (
                                <TableRow key={caja.Id} hover onClick={() => handleVerCaja(caja)}
                                    sx={{ cursor: 'pointer', backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent' }}>
                                    <TableCell><strong>#{caja.Id}</strong></TableCell>
                                    <TableCell sx={{ maxWidth: 300 }}>
                                        <Typography variant="body2" noWrap title={titleCase(caja.Contenido)}>{titleCase(caja.Contenido)}</Typography>
                                    </TableCell>
                                    <TableCell><Chip label={titleCase(caja.Estado)} size="small" color={estadoColor(caja.Id_Estado)} variant="outlined" /></TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{titleCase(caja.Ubicacion) || '-'}</TableCell>
                                    <TableCell><Chip label={caja.Total_Documentos} size="small" color="primary" /></TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{caja.Apertura}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{caja.Responsable}</TableCell>
                                    {(puedeEditar || puedeEliminar) && (
                                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                                            {puedeEditar && String(caja.Id_Estado) !== '-1' && <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEditar(caja)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                                            {puedeEliminar && String(caja.Id_Estado) !== '-1' && <Tooltip title="Anular"><IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, caja })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {total > 0 && (
                    <TablePagination component="div" count={total} page={page}
                        onPageChange={(e, p) => setPage(p)} rowsPerPage={pageSize}
                        onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
                )}
            </TableContainer>

            {/* Dialog Crear/Editar Caja */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle sx={{ backgroundColor: CUST_COLOR, color: 'white', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{dialogMode === 'crear' ? 'Nueva Caja' : 'Editar Caja'}</span>
                    <IconButton onClick={() => setDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <TextField fullWidth size="small" label="Contenido *" multiline rows={3} value={formData.contenido}
                        onChange={e => setFormData(f => ({ ...f, contenido: e.target.value }))} sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                            <TextField fullWidth size="small" label="Precinto" value={formData.precinto}
                                onChange={e => setFormData(f => ({ ...f, precinto: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField fullWidth size="small" label="Vencimiento" type="date" value={formData.vencimiento}
                                onChange={e => setFormData(f => ({ ...f, vencimiento: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}
                        sx={{ backgroundColor: CUST_COLOR, '&:hover': { backgroundColor: '#37474f' } }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : dialogMode === 'crear' ? 'Crear' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Anular */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, caja: null })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: '#d32f2f' }}>Anular Caja</DialogTitle>
                <DialogContent>
                    <Typography>¿Anular la caja <strong>#{deleteDialog.caja?.Id}</strong>?</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Solo se puede anular si no contiene documentos.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, caja: null })} color="inherit">Cancelar</Button>
                    <Button onClick={handleAnular} variant="contained" color="error">Anular</Button>
                </DialogActions>
            </Dialog>

            {/* Detalle Caja Dialog */}
            <Dialog open={detalleOpen} onClose={() => setDetalleOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '4px', minHeight: '70vh' } }}>
                <DialogTitle sx={{ backgroundColor: CUST_COLOR, color: 'white', display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <span>Caja #{detalleCaja?.Id}</span>
                        {detalleCaja && <Chip label={detalleCaja.Estado} size="small" sx={{ ml: 1, color: 'white', borderColor: 'white' }} variant="outlined" />}
                    </Box>
                    <IconButton onClick={() => setDetalleOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {detalleLoading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                    ) : detalleCaja && (
                        <>
                            <Box sx={{ p: 2, backgroundColor: CUST_LIGHT }}>
                                <Grid container spacing={1}>
                                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Contenido:</strong> {detalleCaja.Contenido}</Typography></Grid>
                                    <Grid size={{ xs: 6, sm: 2 }}><Typography variant="body2"><strong>Responsable:</strong> {detalleCaja.Responsable}</Typography></Grid>
                                    <Grid size={{ xs: 6, sm: 2 }}><Typography variant="body2"><strong>Documentos:</strong> {detalleCaja.Total_Documentos}</Typography></Grid>
                                    <Grid size={{ xs: 6, sm: 2 }}><Typography variant="body2"><strong>Apertura:</strong> {detalleCaja.Apertura || '-'}</Typography></Grid>
                                    {detalleCaja.Precinto && <Grid size={{ xs: 6, sm: 3 }}><Typography variant="body2"><strong>Precinto:</strong> {detalleCaja.Precinto}</Typography></Grid>}
                                    {detalleCaja.Vencimiento && <Grid size={{ xs: 6, sm: 3 }}><Typography variant="body2"><strong>Vencimiento:</strong> {detalleCaja.Vencimiento}</Typography></Grid>}
                                    <Grid size={{ xs: 6, sm: 3 }}><Typography variant="body2"><strong>Ubicación:</strong> {detalleCaja.Ubicacion || '-'}</Typography></Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}><Typography variant="body2"><strong>Módulo:</strong> {detalleCaja.Modulo || '-'}</Typography></Grid>
                                </Grid>
                            </Box>
                            <Tabs value={detalleTab} onChange={(e, v) => setDetalleTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                                <Tab label={`Documentos (${detalleDocumentos.length})`} icon={<ArticleIcon />} iconPosition="start" />
                                <Tab label={`Movimientos (${detalleMovimientos.length})`} icon={<SwapHorizIcon />} iconPosition="start" />
                            </Tabs>

                            {/* Tab Documentos */}
                            {detalleTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    {puedeEditar && String(detalleCaja?.Id_Estado) !== '-1' && (
                                        <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ mb: 1 }}
                                            onClick={() => { setDocForm({ tipoDoc: 1, idDoc: '', observaciones: '', fechaArchivo: new Date().toISOString().split('T')[0] }); setDocDialog(true); }}>
                                            Asignar Documento
                                        </Button>
                                    )}
                                    <Table size="small">
                                        <TableHead><TableRow sx={{ backgroundColor: CUST_LIGHT }}>
                                            <TableCell>Tipo</TableCell><TableCell>Descripción</TableCell>
                                            <TableCell>Observaciones</TableCell><TableCell>Fecha Archivo</TableCell>
                                            <TableCell>Responsable</TableCell>
                                            {puedeEliminar && <TableCell align="center">Quitar</TableCell>}
                                        </TableRow></TableHead>
                                        <TableBody>
                                            {detalleDocumentos.length === 0 ? (
                                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>No hay documentos en esta caja</TableCell></TableRow>
                                            ) : detalleDocumentos.map(doc => (
                                                <TableRow key={doc.Id} hover>
                                                    <TableCell><Chip label={doc.Tipo_Nombre} size="small" variant="outlined" /></TableCell>
                                                    <TableCell sx={{ maxWidth: 300 }}><Typography variant="body2" noWrap>{doc.Descripcion_Doc}</Typography></TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{doc.Observaciones || '-'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{doc.Fecha_Archivo || '-'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{doc.Responsable}</TableCell>
                                                    {puedeEliminar && String(detalleCaja?.Id_Estado) !== '-1' && (
                                                        <TableCell align="center">
                                                            <Tooltip title="Desvincular"><IconButton size="small" color="error" onClick={() => setDesvinculaDialog({ open: true, id: doc.Id, desc: doc.Descripcion_Doc })}><LinkOffIcon fontSize="small" /></IconButton></Tooltip>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            {/* Tab Movimientos */}
                            {detalleTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    {puedeEditar && String(detalleCaja?.Id_Estado) !== '-1' && (
                                        <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ mb: 1 }}
                                            onClick={() => { setMovForm({ descripcion: '', tipoMovimiento: -1, estado: detalleCaja.Id_Estado, fecha: new Date().toISOString().split('T')[0] }); setMovDialog(true); }}>
                                            Registrar Movimiento
                                        </Button>
                                    )}
                                    <Table size="small">
                                        <TableHead><TableRow sx={{ backgroundColor: CUST_LIGHT }}>
                                            <TableCell>Fecha</TableCell><TableCell>Descripción</TableCell>
                                            <TableCell>Ubicación</TableCell><TableCell>Estado</TableCell><TableCell>Responsable</TableCell>
                                        </TableRow></TableHead>
                                        <TableBody>
                                            {detalleMovimientos.map(mov => (
                                                <TableRow key={mov.Id}>
                                                    <TableCell>{mov.Fecha}</TableCell>
                                                    <TableCell>{mov.Descripcion}</TableCell>
                                                    <TableCell>{mov.Ubicacion}</TableCell>
                                                    <TableCell><Chip label={mov.Estado} size="small" color={estadoColor(mov.Id_Estado)} variant="outlined" /></TableCell>
                                                    <TableCell>{mov.Responsable}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog Agregar Movimiento */}
            <Dialog open={movDialog} onClose={() => setMovDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Registrar Movimiento</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid size={{ xs: 12 }}>
                            <TextField fullWidth size="small" label="Descripción" value={movForm.descripcion}
                                onChange={e => setMovForm(f => ({ ...f, descripcion: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField select fullWidth size="small" label="Ubicación" value={movForm.tipoMovimiento}
                                onChange={e => setMovForm(f => ({ ...f, tipoMovimiento: e.target.value }))}>
                                {movTipos.map(t => <MenuItem key={t.Id} value={t.Id}>{titleCase(t.Tipo_Mov)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField select fullWidth size="small" label="Estado" value={movForm.estado}
                                onChange={e => setMovForm(f => ({ ...f, estado: e.target.value }))}>
                                {estados.filter(e => e.Id !== -1).map(e => <MenuItem key={e.Id} value={e.Id}>{titleCase(e.Estado)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField fullWidth size="small" label="Fecha" type="date" value={movForm.fecha}
                                onChange={e => setMovForm(f => ({ ...f, fecha: e.target.value }))} InputLabelProps={{ shrink: true }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMovDialog(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleAgregarMov} variant="contained" sx={{ backgroundColor: CUST_COLOR }}>Registrar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Asignar Documento */}
            <Dialog open={docDialog} onClose={() => setDocDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Asignar Documento a Caja</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid size={{ xs: 6 }}>
                            <TextField select fullWidth size="small" label="Tipo de documento" value={docForm.tipoDoc}
                                onChange={e => setDocForm(f => ({ ...f, tipoDoc: parseInt(e.target.value) }))}>
                                <MenuItem value={1}>Exp. Judicial</MenuItem>
                                <MenuItem value={2}>Exp. Administrativo</MenuItem>
                                <MenuItem value={3}>Biblioteca</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Autocomplete
                                freeSolo
                                options={docSearchOptions}
                                getOptionLabel={(opt) => typeof opt === 'string' ? opt : titleCase(opt.label)}
                                filterOptions={(x) => x}
                                loading={docSearching}
                                onInputChange={(e, val) => { handleDocSearch(val); }}
                                onChange={(e, val) => {
                                    if (val && typeof val !== 'string') setDocForm(f => ({ ...f, idDoc: val.id }));
                                }}
                                renderOption={(props, opt) => {
                                    const { key, ...rest } = props;
                                    return <li key={opt.id} {...rest}><Typography variant="body2" noWrap>{opt.label}</Typography></li>;
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" label="Buscar documento"
                                        helperText="Buscar por autos, carátula o código" />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField fullWidth size="small" label="Observaciones" value={docForm.observaciones}
                                onChange={e => setDocForm(f => ({ ...f, observaciones: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField fullWidth size="small" label="Fecha archivo" type="date" value={docForm.fechaArchivo}
                                onChange={e => setDocForm(f => ({ ...f, fechaArchivo: e.target.value }))} InputLabelProps={{ shrink: true }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDocDialog(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleAgregarDoc} variant="contained" sx={{ backgroundColor: CUST_COLOR }}>Asignar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Confirmar Desvincular */}
            <Dialog open={desvinculaDialog.open} onClose={() => setDesvinculaDialog({ open: false, id: null, desc: '' })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: '#d32f2f' }}>Desvincular Documento</DialogTitle>
                <DialogContent>
                    <Typography>¿Desvincular este documento de la caja?</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{titleCase(desvinculaDialog.desc)}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDesvinculaDialog({ open: false, id: null, desc: '' })} color="inherit">Cancelar</Button>
                    <Button onClick={async () => { await handleQuitarDoc(desvinculaDialog.id); setDesvinculaDialog({ open: false, id: null, desc: '' }); }} variant="contained" color="error">Desvincular</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} variant="filled">{snack.text}</Alert>
            </Snackbar>
        </Box>
    );
}
